import React, { useState } from 'react'
import Card from '../../components/Card.jsx'
import FilterBar from '../../components/FilterBar.jsx'

const INSPIRATION_RECIPES = [
  {
    id: 'spaghetti-carbonara',
    title: 'Spaghetti Carbonara',
    type: 'italian',
    prepTime: '10 min',
    cookTime: '20 min',
    servings: 4,
    difficulty: 'Medium',
    image_emoji: '🍝',
    ingredients: [
      '400g spaghetti',
      '200g pancetta or guanciale, diced',
      '4 large eggs',
      '100g Pecorino Romano, finely grated',
      '50g Parmesan, finely grated',
      'Freshly ground black pepper',
      'Salt for pasta water',
    ],
    method: [
      'Bring a large pot of salted water to a boil and cook spaghetti until al dente.',
      'While pasta cooks, fry the pancetta in a large pan over medium heat until crispy. Remove from heat.',
      'Whisk eggs with most of the Pecorino and Parmesan in a bowl. Season generously with black pepper.',
      'Reserve a cup of pasta water before draining. Add drained pasta to the pancetta pan off the heat.',
      'Add the egg mixture quickly, tossing vigorously and adding pasta water a splash at a time to create a silky sauce.',
      'Serve immediately with remaining cheese and extra pepper.',
    ],
    notes: 'The key is removing the pan from heat before adding eggs — too much heat scrambles them.',
  },
  {
    id: 'miso-soup',
    title: 'Miso Soup',
    type: 'asian',
    prepTime: '5 min',
    cookTime: '10 min',
    servings: 2,
    difficulty: 'Easy',
    image_emoji: '🍜',
    ingredients: [
      '600ml dashi stock (or water with kombu)',
      '3 tbsp white or red miso paste',
      '150g silken tofu, cubed',
      '2 spring onions, sliced',
      '1 sheet nori, cut into small pieces',
      'Optional: dried wakame seaweed',
    ],
    method: [
      'Heat the dashi stock in a saucepan until just below boiling.',
      'Dissolve miso paste in a ladleful of hot stock, then stir back into the pan.',
      'Add tofu and warm gently for 2 minutes. Do not boil.',
      'Ladle into bowls and top with spring onions and nori.',
    ],
    notes: 'Never boil miso soup after adding the paste — it destroys the beneficial enzymes.',
  },
  {
    id: 'greek-salad',
    title: 'Greek Salad',
    type: 'mediterranean',
    prepTime: '15 min',
    cookTime: '0 min',
    servings: 4,
    difficulty: 'Easy',
    image_emoji: '🥗',
    ingredients: [
      '4 large ripe tomatoes, roughly chopped',
      '1 cucumber, roughly chopped',
      '1 red onion, thinly sliced',
      '200g Kalamata olives',
      '200g feta cheese, cut into chunks',
      '4 tbsp extra virgin olive oil',
      '1 tsp dried oregano',
      'Salt and pepper to taste',
    ],
    method: [
      'Combine tomatoes, cucumber, and red onion in a large bowl.',
      'Add olives and scatter feta over the top.',
      'Drizzle generously with olive oil.',
      'Sprinkle with oregano, salt and pepper. Serve immediately.',
    ],
    notes: 'Use the ripest tomatoes you can find — they make all the difference.',
  },
  {
    id: 'lentil-soup',
    title: 'Red Lentil Soup',
    type: 'comfort',
    prepTime: '10 min',
    cookTime: '30 min',
    servings: 4,
    difficulty: 'Easy',
    image_emoji: '🍲',
    ingredients: [
      '250g red lentils, rinsed',
      '1 onion, diced',
      '3 garlic cloves, minced',
      '1 tsp ground cumin',
      '1 tsp ground coriander',
      '1/2 tsp turmeric',
      '400g tin chopped tomatoes',
      '1 litre vegetable stock',
      '2 tbsp olive oil',
      'Juice of 1 lemon',
      'Fresh coriander to serve',
    ],
    method: [
      'Heat oil in a large pot. Fry onion until soft, about 5 minutes.',
      'Add garlic and spices, fry for 1 minute until fragrant.',
      'Add lentils, tomatoes, and stock. Bring to a boil.',
      'Simmer for 20–25 minutes until lentils are completely soft.',
      'Blend partially or fully depending on preferred texture.',
      'Stir in lemon juice, season well, and serve with fresh coriander.',
    ],
    notes: 'A squeeze of lemon at the end is essential — it brightens the whole dish.',
  },
  {
    id: 'avocado-toast',
    title: 'Avocado Toast with Poached Egg',
    type: 'light',
    prepTime: '10 min',
    cookTime: '5 min',
    servings: 2,
    difficulty: 'Easy',
    image_emoji: '🥑',
    ingredients: [
      '2 slices sourdough bread',
      '2 ripe avocados',
      '2 eggs',
      '1 tbsp white wine vinegar',
      'Chilli flakes, to taste',
      'Salt and pepper',
      'Lemon juice',
      'Optional: radishes, microgreens',
    ],
    method: [
      'Toast the sourdough until golden and crisp.',
      'Mash avocado with lemon juice, salt and pepper.',
      'Bring a pot of water to a gentle simmer, add vinegar.',
      'Crack each egg into a cup and slide into the simmering water. Cook 3 minutes.',
      'Spread avocado on toast, top with poached egg, chilli flakes and seasoning.',
    ],
    notes: 'The vinegar helps the egg white hold its shape while poaching.',
  },
  {
    id: 'banana-bread',
    title: 'Banana Bread',
    type: 'baking',
    prepTime: '15 min',
    cookTime: '60 min',
    servings: 8,
    difficulty: 'Easy',
    image_emoji: '🍌',
    ingredients: [
      '3 very ripe bananas',
      '75g butter, melted',
      '150g caster sugar',
      '1 egg, beaten',
      '1 tsp vanilla extract',
      '1 tsp bicarbonate of soda',
      'Pinch of salt',
      '190g plain flour',
    ],
    method: [
      'Preheat oven to 175°C. Grease a loaf tin.',
      'Mash bananas thoroughly in a bowl.',
      'Stir in melted butter, sugar, egg, and vanilla.',
      'Add bicarbonate of soda and salt. Fold in flour until just combined.',
      'Pour into prepared tin and bake 55–65 minutes until a skewer comes out clean.',
      'Cool in tin for 10 minutes before turning out.',
    ],
    notes: 'The riper and blacker the bananas, the sweeter and more flavourful the bread.',
  },
  {
    id: 'veggie-stir-fry',
    title: 'Vegetable Stir-Fry with Noodles',
    type: 'vegetarian',
    prepTime: '15 min',
    cookTime: '15 min',
    servings: 3,
    difficulty: 'Easy',
    image_emoji: '🥦',
    ingredients: [
      '300g egg noodles or rice noodles',
      '2 tbsp sesame oil',
      '3 garlic cloves, minced',
      '1 thumb ginger, grated',
      '1 red pepper, sliced',
      '200g broccoli florets',
      '200g beansprouts',
      '3 tbsp soy sauce',
      '1 tbsp oyster sauce (or hoisin for vegetarian)',
      '1 tsp cornflour mixed with 2 tbsp water',
      'Spring onions and sesame seeds to serve',
    ],
    method: [
      'Cook noodles according to packet, drain and set aside.',
      'Heat sesame oil in a wok over high heat until smoking.',
      'Add garlic and ginger, stir-fry 30 seconds.',
      'Add pepper and broccoli, stir-fry 3–4 minutes.',
      'Add beansprouts and noodles, toss everything together.',
      'Add sauces and cornflour mixture, toss until coated and glossy.',
      'Serve topped with spring onions and sesame seeds.',
    ],
    notes: 'High heat is essential for stir-fry — a wok over maximum heat works best.',
  },
  {
    id: 'pasta-primavera',
    title: 'Pasta Primavera',
    type: 'quick',
    prepTime: '10 min',
    cookTime: '15 min',
    servings: 4,
    difficulty: 'Easy',
    image_emoji: '🍝',
    ingredients: [
      '400g penne or farfalle',
      '2 tbsp olive oil',
      '2 garlic cloves, sliced',
      '150g cherry tomatoes, halved',
      '100g peas (fresh or frozen)',
      '100g asparagus, cut into pieces',
      '50g Parmesan, grated',
      'Fresh basil',
      'Salt and pepper',
    ],
    method: [
      'Cook pasta in salted water until al dente. Reserve pasta water.',
      'Heat oil in a large pan. Add garlic and fry 1 minute.',
      'Add tomatoes and cook until they start to break down.',
      'Add peas and asparagus, cook 3 minutes.',
      'Add drained pasta and a splash of pasta water.',
      'Toss with Parmesan, season well, and scatter with basil.',
    ],
    notes: 'Ready in under 25 minutes — ideal for weeknights.',
  },
]

const FILTERS = [
  { key: 'all',           label: 'All types' },
  { key: 'italian',       label: 'Italian',       icon: '🍝' },
  { key: 'asian',         label: 'Asian',         icon: '🍜' },
  { key: 'mediterranean', label: 'Mediterranean', icon: '🥗' },
  { key: 'comfort',       label: 'Comfort food',  icon: '🍲' },
  { key: 'light',         label: 'Light & fresh', icon: '🥑' },
  { key: 'baking',        label: 'Baking',        icon: '🍌' },
  { key: 'vegetarian',    label: 'Vegetarian',    icon: '🥦' },
  { key: 'quick',         label: 'Quick meals',   icon: '⚡' },
]

const SHOW_DEFAULT = 3

// ── Inspiration card ──────────────────────────────────────────────────────────

function InspirationCard({ recipe, onAddToMyRecipes }) {
  const [expanded, setExpanded] = useState(false)

  const diffClass = recipe.difficulty.toLowerCase()

  return (
    <div className="inspiration-card">
      <div className="inspiration-card__header">
        <div className="inspiration-card__emoji">{recipe.image_emoji}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="inspiration-card__title">{recipe.title}</div>
          <div className="inspiration-card__stats">
            <span>⏱ {recipe.prepTime} prep</span>
            <span>🔥 {recipe.cookTime} cook</span>
            <span>👤 Serves {recipe.servings}</span>
            <span className={`difficulty-badge difficulty-badge--${diffClass}`}>
              {recipe.difficulty}
            </span>
          </div>
        </div>
      </div>

      <div className="inspiration-card__ingredients-count">
        {recipe.ingredients.length} ingredients
      </div>

      <button
        className="inspiration-card__toggle"
        onClick={() => setExpanded(e => !e)}
      >
        {expanded ? 'Hide recipe ▲' : 'See full recipe ▼'}
      </button>

      {expanded && (
        <div className="inspiration-card__full">
          <div className="inspiration-card__section-label">Ingredients</div>
          <ul className="inspiration-card__ingredients">
            {recipe.ingredients.map((ing, i) => <li key={i}>{ing}</li>)}
          </ul>

          <div className="inspiration-card__section-label">Method</div>
          <ol className="inspiration-card__method">
            {recipe.method.map((step, i) => <li key={i}>{step}</li>)}
          </ol>

          {recipe.notes && (
            <div className="inspiration-card__note">
              💡 {recipe.notes}
            </div>
          )}
        </div>
      )}

      <button
        className="inspiration-card__add-btn"
        onClick={() => onAddToMyRecipes(recipe)}
      >
        + Add to My Recipes
      </button>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function RecipeInspiration({ onPreFill }) {
  const [activeFilter,  setActiveFilter]  = useState('all')
  const [showAll,       setShowAll]       = useState(false)
  const [singleRandom,  setSingleRandom]  = useState(null)

  const filtered = activeFilter === 'all'
    ? INSPIRATION_RECIPES
    : INSPIRATION_RECIPES.filter(r => r.type === activeFilter)

  const visible = singleRandom
    ? [singleRandom]
    : showAll
      ? filtered
      : filtered.slice(0, SHOW_DEFAULT)

  const hiddenCount = filtered.length - SHOW_DEFAULT

  function handleFilter(key) {
    setActiveFilter(key)
    setSingleRandom(null)
    setShowAll(false)
  }

  function handleSurprise() {
    const pool = activeFilter === 'all'
      ? INSPIRATION_RECIPES
      : INSPIRATION_RECIPES.filter(r => r.type === activeFilter)
    setSingleRandom(pool[Math.floor(Math.random() * pool.length)])
  }

  function handleAddToMyRecipes(recipe) {
    if (onPreFill) onPreFill(recipe)
  }

  return (
    <Card label="Cooking" title="Recipe Inspiration">
      <div className="inspiration-controls">
        <FilterBar filters={FILTERS} active={activeFilter} onChange={handleFilter} />
        <button className="inspiration-random-btn" onClick={handleSurprise}>
          🎲 Surprise me
        </button>
        {singleRandom && (
          <button className="ghost-btn" style={{ fontSize: '0.78rem' }} onClick={() => setSingleRandom(null)}>
            Show all again
          </button>
        )}
      </div>

      {visible.map(recipe => (
        <InspirationCard
          key={recipe.id}
          recipe={recipe}
          onAddToMyRecipes={handleAddToMyRecipes}
        />
      ))}

      {!singleRandom && hiddenCount > 0 && !showAll && (
        <button className="cook-show-more" onClick={() => setShowAll(true)}>
          Show {hiddenCount} more ▼
        </button>
      )}
      {!singleRandom && showAll && filtered.length > SHOW_DEFAULT && (
        <button className="cook-show-more" onClick={() => setShowAll(false)}>
          Show less ▲
        </button>
      )}
    </Card>
  )
}
